/**
 * Salle d'audience — Procès virtuel SmartLawyer
 * Route : /tribunal/:trialId
 *
 * Plein écran (h-dvh). Avant l'audience : dossier + composition de la cour
 * (WaitingRoom). Pendant : dossier permanent à gauche (CaseDossier), la salle
 * en décor (CourtDiorama, eau-forte coloriée) derrière le procès-verbal (Transcript), et chaque
 * intervention des autres passe au premier plan quelques secondes
 * (Spotlight). Le pupitre de l'avocat reste toujours au-dessus. Le tour de
 * parole est strict (le président donne la parole) ; seuls les incidents de
 * procédure peuvent être soulevés à tout moment.
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence, MotionConfig, useReducedMotion } from 'framer-motion';
import { PublicHeader } from '@/components/jurisprudence';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { getIdToken } from '@/lib/firebase';
import { useTranslation, Trans } from 'react-i18next';
import i18n, { getDateLocale } from '@/i18n';
import { CourtContext, CourtDiorama, TrialAvatar, courtOf, witnessVariant } from './Courtroom';
import { CaseDossier, dossierTabs, type DossierTab } from './CaseDossier';
import { Spotlight, expressionFor, useSpotlightQueue } from './Spotlight';
import { Transcript } from './Transcript';
import { WaitingRoom } from './WaitingRoom';
import {
  DEBATE_PHASES, readLocal, timeoutSignal, writeLocal, withLang,
  type Side, type Trial, type TrialMessage,
} from './trialTypes';

// ── Constantes ────────────────────────────────────────────────────────────────

/** Codes `verdict` backend (jamais traduits côté serveur) → `tribunal.verdict_values.*`. */
const VERDICT_KEYS: Record<string, string> = {
  'COUPABLE': 'guilty', 'NON COUPABLE': 'not_guilty', 'PARTIELLEMENT COUPABLE': 'partially_guilty',
};

/** Seuil (px) sous lequel on considère que le lecteur suit le bas du journal. */
const STICKY_BOTTOM_PX = 120;

// ── Utilitaires ──────────────────────────────────────────────────────────────

/** Ferme une modale / un tiroir avec Échap. */
function useEscape(active: boolean, onClose: () => void) {
  useEffect(() => {
    if (!active) return;
    const h = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [active, onClose]);
}

/** Compteur animé 0 → target (immédiat si mouvement réduit). */
function useCountUp(target: number, delayMs: number) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? target : 0);
  useEffect(() => {
    if (reduce) { setValue(target); return; }
    let raf = 0;
    let start = 0;
    const timer = setTimeout(() => {
      const step = (ts: number) => {
        if (!start) start = ts;
        const p = Math.min(1, (ts - start) / 1200);
        setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
    }, delayMs);
    return () => { clearTimeout(timer); cancelAnimationFrame(raf); };
  }, [target, delayMs, reduce]);
  return value;
}

// ── Composants ────────────────────────────────────────────────────────────────

/**
 * Avatar d'un intervenant (cf. TrialAvatar) : `speaking` = halo + ondes,
 * `thinking` = l'IA rédige, `strikeKey` = coup de marteau du juge.
 */
function SpeakerAvatar({
  role, speaking = false, size = 44, thinking = false, strikeKey,
}: { role: string; speaking?: boolean; size?: number; idle?: boolean; thinking?: boolean; strikeKey?: string | null }) {
  return <TrialAvatar role={role} size={size} speaking={speaking} thinking={thinking} strikeKey={strikeKey} />;
}

// ── En-tête : étapes, parties, salle ─────────────────────────────────────────

/** Étapes nommées (écran large) ; « Étape 2/6 · Témoins » ailleurs. */
function PhaseStepper({ phase }: { phase: string }) {
  const { t } = useTranslation();
  const idx = DEBATE_PHASES.indexOf(phase);
  return (
    <>
      <ol aria-label={t('tribunal.court.steps_label')} className="hidden 2xl:flex items-center gap-1 text-xs font-semibold">
        {DEBATE_PHASES.map((p, i) => (
          <li key={p} className="flex items-center gap-1">
            {i > 0 && <span aria-hidden="true" className="w-3 h-px bg-border" />}
            <span
              aria-current={i === idx ? 'step' : undefined}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full ${
                i === idx ? 'bg-foreground text-background' : i < idx ? 'text-muted-foreground' : 'text-muted-foreground/80'
              }`}
            >
              {i < idx
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-[#4f7a6a] dark:text-[#7fa595]" aria-hidden="true"><path d="M5 12l5 5L20 7" /></svg>
                : <span className="tabular-nums">{i + 1}</span>}
              {t(`tribunal.phases.${p}`)}
            </span>
          </li>
        ))}
      </ol>
      <span className="2xl:hidden text-xs text-muted-foreground truncate">
        {idx >= 0 && <>{t('tribunal.room.step', { n: idx + 1, total: DEBATE_PHASES.length })} · </>}
        {t(`tribunal.phases_long.${phase}`, phase)}
      </span>
    </>
  );
}

/** Pastille d'un avocat : avatar, nom, interventions faites dans l'étape. */
function PartyChip({
  side, name, active, mine, done, required,
}: { side: Side; name: string | null; active: boolean; mine: boolean; done: number; required: number }) {
  const { t } = useTranslation();
  const isP = side === 'prosecution';
  return (
    <div
      className={`flex items-center gap-2 pl-1 pr-3 py-1 rounded-full transition-colors ${
        active ? (isP ? 'bg-[#f6e9e3] dark:bg-[#3a150f]/40 ring-2 ring-[#9b3a2e]' : 'bg-[#e9eef1] dark:bg-[#18242d]/40 ring-2 ring-[#3d5a73]') : 'bg-muted/60'
      }`}
    >
      <TrialAvatar role={side} size={30} speaking={active} />
      <div className="leading-tight min-w-0">
        <div className={`text-[10px] font-bold uppercase tracking-wider ${isP ? 'text-[#9b3a2e] dark:text-[#c47a68]' : 'text-[#3d5a73] dark:text-[#8aa3b8]'}`}>
          {t(`tribunal.seats.${side}`)}{mine && <span className="ml-1 normal-case tracking-normal text-muted-foreground">{t('tribunal.participant.you')}</span>}
        </div>
        <div className="text-xs font-bold text-foreground truncate max-w-[130px]">{name || t('tribunal.seats.vacant')}</div>
      </div>
      {required > 0 && (
        <span className="flex gap-0.5 ml-1" role="img" aria-label={t('tribunal.court.interventions_aria', { done: Math.min(done, required), required })}>
          {Array.from({ length: required }).map((_, i) => (
            <span key={i} className={`w-3.5 h-1.5 rounded-full ${i < done ? (isP ? 'bg-[#9b3a2e]' : 'bg-[#3d5a73]') : 'bg-border'}`} />
          ))}
        </span>
      )}
    </div>
  );
}

/** Ligne « qui a la parole » (mobile, et spectateurs). */
function turnLine(t: (k: string, o?: any) => string, trial: Trial, myRole: Side | null): { text: string; tone: 'mine' | Side | 'court' } {
  const sp = trial.current_speaker as Side | null;
  if (trial.status === 'completed') return { text: t('tribunal.turn.rendering_verdict'), tone: 'court' };
  if (trial.pending_actor === 'judge') return { text: t('tribunal.pending.judge', { name: t('tribunal.roles.president') }), tone: 'court' };
  if (trial.pending_actor === 'witness') return { text: t('tribunal.pending.witness'), tone: 'court' };
  if (!sp) return { text: trial.current_phase === 'deliberation' ? t('tribunal.turn.deliberating') : t('tribunal.turn.rendering_verdict'), tone: 'court' };
  if (myRole === sp) return { text: t('tribunal.court.your_turn'), tone: 'mine' };
  const name = sp === 'prosecution' ? trial.prosecution_name : trial.defense_name;
  return { text: `${t(`tribunal.turn.${sp}_speaks`)}${name ? ` — ${name}` : ''}`, tone: sp };
}

// ── Pupitre de l'avocat ───────────────────────────────────────────────────────

type ComposeMode = 'plead' | 'question' | 'evidence';
const OTHER_EVIDENCE = '__other__';

function Composer({
  trial, myRole, mode, setMode, witnessId, setWitnessId, evidenceId, setEvidenceId, textareaRef, onAction, onKick,
}: {
  trial: Trial;
  myRole: Side;
  mode: ComposeMode;
  setMode: (m: ComposeMode) => void;
  witnessId: string | null;
  setWitnessId: (id: string | null) => void;
  evidenceId: string | null;
  setEvidenceId: (id: string | null) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement>;
  /** Recharge l'affaire complète une fois l'action terminée côté serveur. */
  onAction: () => void;
  /** Déclenche un poll anticipé : le message de l'avocat est enregistré avant les appels LLM. */
  onKick: () => void;
}) {
  const { t } = useTranslation();
  const [text,            setText]            = useState('');
  const [otherTitle,      setOtherTitle]      = useState('');
  const [incidentOpen,    setIncidentOpen]    = useState(false);
  const [citeOpen,        setCiteOpen]        = useState(false);
  const [grounds,         setGrounds]         = useState('');
  const [loading,         setLoading]         = useState(false);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [phaseLoading,    setPhaseLoading]    = useState(false);
  const [error,           setError]           = useState('');

  const phase         = trial.current_phase;
  const isExamination = phase === 'examination';
  const gc            = trial.generated_case;
  const witnesses     = gc?.witnesses ?? [];
  const filedTitles   = new Set(trial.evidence_list.map(e => e.title.trim().toLowerCase()));
  const evidenceLeft  = (gc?.available_evidence ?? []).filter(e => !filedTitles.has(e.title.trim().toLowerCase()));

  // L'interrogatoire des témoins n'existe qu'en phase d'interrogatoire
  useEffect(() => {
    if (!isExamination && mode === 'question') setMode('plead');
  }, [isExamination, mode, setMode]);

  // « Ce n'est pas votre tour » & co. disparaissent dès que la situation change
  useEffect(() => { setError(''); }, [trial.current_speaker, trial.current_phase]);

  const post = async (url: string, body: object) => {
    const token = await getIdToken();
    if (!token) throw new Error(t('tribunal.controls.session_expired_plead'));
    const req = fetch(withLang(url), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(body),
    });
    onKick();
    const r = await req;
    if (!r.ok) {
      const raw = await r.text();
      let msg = raw;
      try { msg = JSON.parse(raw).detail || raw; } catch { /* réponse non-JSON */ }
      throw new Error(msg || t('tribunal.controls.error_generic'));
    }
  };

  const fail = (e: unknown) => setError((e as Error)?.message || t('tribunal.controls.error_generic'));

  const isMyTurn      = trial.current_speaker === myRole;
  const debatesClosed = !trial.current_speaker;
  const aiBusy        = !!trial.pending_actor;
  const chosenEvidence = evidenceLeft.find(e => e.id === evidenceId) ?? null;
  const otherEvidence  = evidenceId === OTHER_EVIDENCE;
  const witness        = witnesses.find(w => w.id === witnessId) ?? null;

  // Le dépôt d'une pièce n'est une intervention (tour de parole) qu'en phase « Pièces »
  const evidenceNeedsTurn = phase === 'evidence';
  const evidenceTitle = chosenEvidence?.title ?? (otherEvidence ? otherTitle.trim() : '');
  const sendDisabled =
    loading || aiBusy ||
    (mode === 'evidence'
      ? !evidenceTitle || (evidenceNeedsTurn && !isMyTurn)
      : !text.trim() || !isMyTurn || (mode === 'question' && !witness));

  const handleSend = async () => {
    if (sendDisabled) return;
    setLoading(true); setError('');
    const content = text.trim();
    try {
      if (mode === 'evidence') {
        await post(`/api/trials/${trial.id}/evidence`, {
          title: evidenceTitle,
          description: content || chosenEvidence?.description || '',
        });
        setEvidenceId(null); setOtherTitle(''); setText('');
      } else {
        setText('');  // optimiste : le texte revient en cas d'échec
        await post(`/api/trials/${trial.id}/message`, {
          content,
          witness_id: mode === 'question' ? witness?.id : undefined,
        });
      }
      onAction();
    } catch (e) {
      if (mode !== 'evidence') setText(prev => prev || content);
      fail(e);
    } finally { setLoading(false); }
  };

  // Phase « Pièces » sans pièce : l'avocat le déclare, ce qui vaut intervention.
  const handleNoEvidence = async () => {
    if (loading || aiBusy || !isMyTurn) return;
    setLoading(true); setError('');
    try {
      await post(`/api/trials/${trial.id}/message`, { content: t('tribunal.court.no_evidence_statement') });
      setEvidenceId(null); setOtherTitle('');
      onAction();
    } catch (e) { fail(e); }
    finally { setLoading(false); }
  };

  const handleIncident = async () => {
    const g = grounds.trim();
    if (!g || incidentLoading) return;
    setIncidentLoading(true); setError('');
    try {
      await post(`/api/trials/${trial.id}/incident`, { grounds: g });
      setGrounds(''); setIncidentOpen(false);
      onAction();
    } catch (e) { fail(e); }
    finally { setIncidentLoading(false); }
  };

  const handleNextPhase = async () => {
    setPhaseLoading(true); setError('');
    try {
      await post(`/api/trials/${trial.id}/next-phase`, {});
    } catch (e) {
      fail(e);  // le refus est aussi annoncé par le greffier dans le journal
    } finally {
      setPhaseLoading(false);
      onAction();
    }
  };

  // Le tribunal — pas un avocat seul — décide de clore une étape : chaque
  // partie doit s'être exprimée, et en plaidoiries de clôture la défense a
  // la parole en dernier (CPP art. 460). Même règle que le backend.
  const phaseRequiresSpeech = phase !== 'deliberation' && phase !== 'waiting';
  const statements = trial.messages.filter(
    m => m.phase === phase && m.msg_type === 'statement' && (m.role === 'prosecution' || m.role === 'defense'),
  );
  const spoken      = new Set(statements.map(m => m.role));
  const bothSpoke   = spoken.has('prosecution') && spoken.has('defense');
  const lastWordOk  = phase !== 'closing' || statements[statements.length - 1]?.role === 'defense';
  const prog = trial.phase_progress && trial.phase_progress.phase === phase ? trial.phase_progress : null;
  const phaseGateOpen = !phaseRequiresSpeech || (bothSpoke && lastWordOk);
  const phaseGateHint = phaseGateOpen ? '' :
    !bothSpoke ? t('tribunal.controls.next_phase_locked_hint') : t('tribunal.controls.last_word_hint');

  const nextPhaseButton = (
    <button
      onClick={handleNextPhase}
      disabled={phaseLoading || aiBusy || !phaseGateOpen}
      title={phaseGateHint || undefined}
      className="shrink-0 leading-tight text-xs font-bold px-3 h-9 rounded-lg bg-[#f3e3c0] text-[#553913] dark:bg-[#553913]/30 dark:text-[#e9cf98] hover:bg-[#e9cf98] dark:hover:bg-[#553913]/50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
    >
      {phaseLoading ? '…' : t('tribunal.controls.next_phase')}
    </button>
  );

  const errorLine = error && (
    <p role="alert" className="mt-2 text-xs font-semibold text-[#9b3a2e] dark:text-[#c47a68]">{error}</p>
  );

  const shell = 'rounded-2xl border border-border bg-card/95 backdrop-blur shadow-xl p-3';

  // Débats clos (délibéré) : plus de plaidoirie, seulement le prononcé du verdict.
  if (debatesClosed) {
    return (
      <div className={shell}>
        <div className="flex items-center gap-3">
          <p className="text-sm text-muted-foreground flex-1">{t('tribunal.controls.debates_closed')}</p>
          {nextPhaseButton}
        </div>
        {errorLine}
      </div>
    );
  }

  const isP = myRole === 'prosecution';
  const sideBtn = isP ? 'bg-[#9b3a2e] hover:bg-[#7e2c22]' : 'bg-[#3d5a73] hover:bg-[#2f4659]';
  const sideRing = isP ? 'focus-within:ring-[#9b3a2e]' : 'focus-within:ring-[#3d5a73]';
  const modes: { id: ComposeMode; label: string }[] = [
    { id: 'plead', label: t('tribunal.court.mode_plead') },
    ...(isExamination ? [{ id: 'question' as const, label: t('tribunal.court.mode_question') }] : []),
    { id: 'evidence', label: t('tribunal.court.mode_evidence') },
  ];
  const sendLabel =
    mode === 'evidence' ? t('tribunal.court.send_evidence') :
    mode === 'question' ? (witness ? t('tribunal.court.send_question') : t('tribunal.court.choose_witness_short')) :
    t('tribunal.controls.send');
  const placeholder =
    mode === 'evidence' ? t('tribunal.court.evidence_note_placeholder') :
    mode === 'question' ? (witness ? t('tribunal.court.question_to_placeholder', { name: witness.name }) : t('tribunal.court.choose_witness_first')) :
    t('tribunal.controls.speak_placeholder');
  const waitingTurn = !isMyTurn && !(mode === 'evidence' && !evidenceNeedsTurn);
  const otherSide: Side = isP ? 'defense' : 'prosecution';
  const prog2 = prog;

  return (
    <div className={shell}>
      {/* Où en est-on */}
      <div className="flex items-center gap-2 mb-2 min-h-[36px]">
        {isMyTurn && !aiBusy ? (
          <p className="text-xs min-w-0">
            <strong className={isP ? 'text-[#9b3a2e] dark:text-[#c47a68]' : 'text-[#3d5a73] dark:text-[#8aa3b8]'}>{t('tribunal.court.your_turn')}</strong>
            {prog2 && prog2.required > 1 && (
              <span className="text-muted-foreground"> · {t('tribunal.court.turn_n', { n: Math.min((isP ? prog2.prosecution : prog2.defense) + 1, prog2.required), required: prog2.required })}</span>
            )}
            <span className="hidden sm:inline text-muted-foreground"> — {t(`tribunal.court.hints.${phase}`, '')}</span>
          </p>
        ) : (
          <p className="text-xs font-semibold text-[#553913] dark:text-[#e0b866] min-w-0">
            {aiBusy
              ? (trial.pending_actor === 'witness' ? t('tribunal.pending.witness') : t('tribunal.pending.judge', { name: t('tribunal.roles.president') }))
              : t(`tribunal.turn.${otherSide}_speaks`)}
            <span className="font-normal text-muted-foreground"> · {t('tribunal.court.draft_kept')}</span>
          </p>
        )}
        <span className="ml-auto" />
        {/* Sur mobile, le bouton n'apparaît que lorsqu'il est utilisable */}
        <span className={`shrink-0 ${phaseGateOpen ? 'inline-flex' : 'hidden sm:inline-flex'}`}>{nextPhaseButton}</span>
      </div>

      {/* Type d'intervention + outils */}
      <div className="flex items-center gap-1.5 sm:gap-2 mb-2">
        <div role="radiogroup" aria-label={t('tribunal.court.mode_label')} className="flex flex-1 sm:flex-none gap-0.5 p-0.5 rounded-xl bg-muted">
          {modes.map(m => (
            <button
              key={m.id}
              role="radio"
              aria-checked={mode === m.id}
              onClick={() => setMode(m.id)}
              className={`flex-1 sm:flex-none h-9 px-2 sm:px-3 rounded-[10px] text-[13px] font-bold whitespace-nowrap transition-colors ${
                mode === m.id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {m.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setCiteOpen(o => !o)}
          aria-expanded={citeOpen}
          aria-label={t('tribunal.court.cite')}
          className={`h-9 px-2.5 sm:px-3 rounded-lg text-[13px] font-semibold flex items-center gap-1.5 transition-colors ${citeOpen ? 'bg-muted text-foreground' : 'text-muted-foreground hover:text-foreground'}`}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-4-4" /></svg>
          <span className="hidden sm:inline">{t('tribunal.court.cite')}</span>
        </button>
        <button
          onClick={() => setIncidentOpen(o => !o)}
          aria-expanded={incidentOpen}
          aria-label={t('tribunal.court.incident')}
          className={`sm:ml-auto h-9 px-2.5 sm:px-3 rounded-lg text-[13px] font-bold flex items-center gap-1.5 transition-colors border ${
            incidentOpen ? 'bg-[#9b3a2e] text-white border-[#9b3a2e]' : 'border-[#e2c3b5] dark:border-[#5e2018] text-[#9b3a2e] dark:text-[#c47a68] hover:bg-[#f6e9e3] dark:hover:bg-[#3a150f]/30'
          }`}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3l9 16H3z" /><path d="M12 10v4M12 17h.01" /></svg>
          <span className="hidden sm:inline">{t('tribunal.court.incident')}</span>
        </button>
      </div>

      {/* Incident de procédure — à tout moment, sans perdre le brouillon en cours */}
      <AnimatePresence initial={false}>
        {incidentOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mb-2 rounded-xl border border-[#e2c3b5] dark:border-[#7e2c22] bg-[#f6e9e3] dark:bg-[#3a150f]/20 p-3">
              <div className="flex flex-col sm:flex-row gap-2">
                <input
                  type="text"
                  autoFocus
                  value={grounds}
                  onChange={e => setGrounds(e.target.value)}
                  placeholder={t('tribunal.controls.incident_placeholder')}
                  aria-label={t('tribunal.controls.raise_incident')}
                  className="flex-1 min-w-0 rounded-lg border border-[#e2c3b5] dark:border-[#7e2c22] bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c47a68]"
                  onKeyDown={e => { if (e.key === 'Enter') handleIncident(); if (e.key === 'Escape') setIncidentOpen(false); }}
                />
                <button
                  onClick={handleIncident}
                  disabled={incidentLoading || !grounds.trim()}
                  className="shrink-0 h-11 sm:h-auto px-4 py-2 rounded-lg bg-[#9b3a2e] text-white font-bold text-sm hover:bg-[#7e2c22] disabled:opacity-40 transition-colors"
                >
                  {incidentLoading ? '…' : t('tribunal.controls.submit_incident')}
                </button>
              </div>
              <p className="text-[11px] text-[#5e2018]/80 dark:text-[#d4a090]/80 mt-1.5">{t('tribunal.controls.incident_hint')}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence initial={false}>
        {citeOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
            <div className="mb-2 rounded-xl border border-border p-3 max-h-[40dvh] overflow-y-auto">
              <JurisprudencePanel
                onCite={(citation) => {
                  if (mode === 'evidence') setMode('plead');
                  setText(prev => prev ? `${prev}\n\n${citation}` : citation);
                  setCiteOpen(false);
                  textareaRef.current?.focus();
                }}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Zone de saisie : destinataire / pièce, texte, envoi */}
      <div className={`rounded-xl border border-border bg-background ring-0 focus-within:ring-2 ${sideRing}`}>
        {mode === 'question' && (
          <div className="flex items-center gap-2 px-3 pt-2.5 text-[13px] flex-wrap">
            <span className="text-muted-foreground">{t('tribunal.court.question_to')}</span>
            {witness ? (
              <span className="flex items-center gap-1 font-bold bg-[#e9f1ec] dark:bg-[#1b2c25]/40 text-[#2c463c] dark:text-[#9cbfae] pl-2.5 pr-1 py-0.5 rounded-lg">
                {witness.name}
                <button onClick={() => setWitnessId(null)} aria-label={t('tribunal.court.change_witness')} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#d8e6de] dark:hover:bg-[#2c463c]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </span>
            ) : (
              <select
                value=""
                onChange={e => setWitnessId(e.target.value || null)}
                aria-label={t('tribunal.controls.choose_witness')}
                className="h-9 rounded-lg border border-border bg-muted/40 px-2 text-sm"
              >
                <option value="">{t('tribunal.controls.choose_witness')}</option>
                {witnesses.map(w => <option key={w.id} value={w.id}>{w.name} ({w.role})</option>)}
              </select>
            )}
          </div>
        )}
        {mode === 'evidence' && (
          <div className="flex items-center gap-2 px-3 pt-2.5 text-[13px] flex-wrap">
            <span className="text-muted-foreground">{t('tribunal.court.evidence_label')}</span>
            {chosenEvidence ? (
              <span className="flex items-center gap-1 font-bold bg-[#e9f1ec] dark:bg-[#1b2c25]/40 text-[#2c463c] dark:text-[#9cbfae] pl-2.5 pr-1 py-0.5 rounded-lg">
                {chosenEvidence.title}
                <button onClick={() => setEvidenceId(null)} aria-label={t('tribunal.court.change_evidence')} className="w-6 h-6 flex items-center justify-center rounded hover:bg-[#d8e6de] dark:hover:bg-[#2c463c]">
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </span>
            ) : (
              <select
                value={otherEvidence ? OTHER_EVIDENCE : ''}
                onChange={e => setEvidenceId(e.target.value || null)}
                aria-label={t('tribunal.court.choose_evidence')}
                className="h-9 rounded-lg border border-border bg-muted/40 px-2 text-sm max-w-full"
              >
                <option value="">{t('tribunal.court.choose_evidence')}</option>
                {evidenceLeft.map(e => <option key={e.id} value={e.id}>{e.title}</option>)}
                <option value={OTHER_EVIDENCE}>{t('tribunal.court.other_evidence')}</option>
              </select>
            )}
            {otherEvidence && (
              <input
                type="text"
                value={otherTitle}
                onChange={e => setOtherTitle(e.target.value)}
                placeholder={t('tribunal.controls.evidence_title_placeholder')}
                aria-label={t('tribunal.controls.evidence_title_placeholder')}
                className="flex-1 min-w-[200px] rounded-lg border border-border bg-background px-2 py-1 text-[13px]"
              />
            )}
          </div>
        )}
        <label htmlFor="trial-composer" className="sr-only">{placeholder}</label>
        <textarea
          id="trial-composer"
          ref={textareaRef}
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder={placeholder}
          rows={2}
          className="block w-full resize-none bg-transparent px-3 py-2.5 text-[15px] leading-relaxed text-foreground placeholder:text-muted-foreground focus:outline-none"
          onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleSend(); }}
        />
        <div className="flex items-center gap-3 px-3 pb-2.5">
          {mode === 'evidence' && evidenceNeedsTurn && isMyTurn && !aiBusy ? (
            <button
              type="button"
              onClick={handleNoEvidence}
              disabled={loading}
              className="h-11 px-1 text-sm font-semibold text-muted-foreground underline underline-offset-4 hover:text-foreground disabled:opacity-40 text-left leading-tight"
            >
              {t('tribunal.court.no_evidence')}
            </button>
          ) : (
            <span className="hidden sm:inline text-xs text-muted-foreground">
              {waitingTurn ? t('tribunal.court.wait_your_turn') : t('tribunal.court.send_shortcut')}
            </span>
          )}
          <button
            onClick={handleSend}
            disabled={sendDisabled}
            className={`ml-auto h-11 px-5 rounded-lg text-white text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${sideBtn}`}
          >
            {loading ? '…' : sendLabel}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
          </button>
        </div>
      </div>
      {errorLine}
    </div>
  );
}

// ── Recherche jurisprudence ───────────────────────────────────────────────────

type JurSource = 'jurisprudence' | 'cnil' | 'cjue';

interface JurResult {
  id:       string;
  label:    string;   // ex: "Cass. civ. 1re — 15 nov. 2023 — n°21-14789"
  summary:  string;   // extrait / solution
  citation: string;   // texte prêt à coller
  url?:     string;
}

/** Citation prête à coller dans un message : lien réel si une URL existe, sinon texte simple. */
function buildCitation(label: string, summary: string, url?: string): string {
  const ellipsis = summary.length >= 200 ? '…' : '';
  return url
    ? `[${label}](${url}) : « ${summary}${ellipsis} »`
    : `[${label} : « ${summary}${ellipsis} »]`;
}

function formatFicheResult(r: any): JurResult {
  const jur   = r.jurisdiction || '';
  const short = jur.includes('cassation') ? 'Cass.' : jur.includes('appel') ? 'CA' : jur.includes('État') ? 'CE' : jur;
  const ch    = r.chamber || '';
  const date  = r.decision_date ? new Date(r.decision_date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'short', year: 'numeric' }) : '';
  const num   = r.number || r.judilibre_id || '';
  const label = [short, ch, date, num ? `n°${num}` : ''].filter(Boolean).join(' — ');
  const summary = (r.solution_text || r.solution || r.probleme || '').slice(0, 200);
  const citation = buildCitation(label, summary, r.url);
  return { id: r.id || r.judilibre_id || label, label, summary, citation, url: r.url };
}

function formatCnilResult(r: any): JurResult {
  const num   = r.num || '';
  const date  = r.date_texte ? r.date_texte.slice(0, 10) : '';
  const label = `${i18n.t('tribunal.jur.cnil_label', { num })}${date ? ` — ${date}` : ''}`;
  const summary = (r.extrait || r.titre_long || '').slice(0, 200);
  const url = `/cnil/deliberations/${num}`;
  const citation = buildCitation(label, summary, url);
  return { id: num || label, label, summary, citation, url };
}

function formatCjueResult(r: any): JurResult {
  const num   = r.numero || r.num || r.affaire || '';
  const date  = r.date || r.date_arret || '';
  const label = `${i18n.t('tribunal.jur.cjue_label', { num })}${date ? ` (${date})` : ''}`;
  const summary = (r.sommaire || r.extrait || r.solution || '').slice(0, 200);
  // Pas d'URL fiable pour une décision CJUE ici : citation en texte, sans lien fabriqué.
  const citation = buildCitation(label, summary);
  return { id: num || label, label, summary, citation };
}

async function searchJurisprudence(query: string, source: JurSource, limit = 8): Promise<JurResult[]> {
  if (!query.trim()) return [];

  const endpoints: Record<JurSource, { url: string; method: 'POST'; body: object }> = {
    jurisprudence: {
      url:    '/api/public/fiches/search',
      method: 'POST',
      body:   { query, limit },
    },
    cnil: {
      url:    '/api/cnil/search',
      method: 'POST',
      body:   { query, limit },
    },
    cjue: {
      url:    '/api/cjue/search',
      method: 'POST',
      body:   { query, limit },
    },
  };

  const { url, method, body } = endpoints[source];
  const r = await fetch(url, {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!r.ok) return [];
  const data = await r.json();
  const results = data.results ?? data.items ?? [];

  return results.map((item: any) => {
    if (source === 'cnil') return formatCnilResult(item);
    if (source === 'cjue') return formatCjueResult(item);
    return formatFicheResult(item);
  });
}

function JurisprudencePanel({ onCite }: { onCite: (citation: string) => void }) {
  const { t } = useTranslation();
  const [source,  setSource]  = useState<JurSource>('jurisprudence');
  const [query,   setQuery]   = useState('');
  const [results, setResults] = useState<JurResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [cited,   setCited]   = useState<Set<string>>(new Set());

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const res = await searchJurisprudence(query, source);
      setResults(res);
    } finally {
      setLoading(false);
    }
  };

  const handleCite = (r: JurResult) => {
    onCite(r.citation);
    setCited(prev => new Set([...prev, r.id]));
  };

  const sourceLabels: Record<JurSource, string> = {
    jurisprudence: t('tribunal.jur.source_jurisprudence'),
    cnil:          t('tribunal.jur.source_cnil'),
    cjue:          t('tribunal.jur.source_cjue'),
  };

  return (
    <div className="space-y-3">
      {/* Sélecteur source */}
      <div className="flex gap-2">
        {(Object.entries(sourceLabels) as [JurSource, string][]).map(([v, l]) => (
          <button
            key={v}
            onClick={() => { setSource(v); setResults([]); }}
            className={`text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-all ${
              source === v
                ? 'bg-[#a8742a] text-white'
                : 'bg-muted text-muted-foreground hover:bg-gray-200 dark:hover:bg-gray-700'
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {/* Barre de recherche */}
      <div className="flex gap-2">
        <input
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') handleSearch(); }}
          placeholder={
            source === 'jurisprudence' ? t('tribunal.jur.placeholder_jurisprudence') :
            source === 'cnil'          ? t('tribunal.jur.placeholder_cnil') :
                                         t('tribunal.jur.placeholder_cjue')
          }
          className="flex-1 rounded-xl border border-[#e9cf98] dark:border-[#6e4a1a] bg-[#f8eedb] dark:bg-[#33220b]/20 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#d9a441] dark:text-gray-100"
        />
        <button
          onClick={handleSearch}
          disabled={loading || !query.trim()}
          className="px-4 py-2 rounded-xl bg-[#a8742a] text-white font-bold text-sm hover:bg-[#8f6122] disabled:opacity-50 transition-colors"
        >
          {loading ? '⏳' : '🔍'}
        </button>
      </div>

      {/* Résultats */}
      {results.length > 0 && (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {results.map(r => (
            <div
              key={r.id}
              className="bg-card rounded-xl border border-border p-3"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] font-bold text-[#8f6122] dark:text-[#d9a441] mb-1 leading-snug">
                    {r.label}
                  </p>
                  {r.summary && (
                    <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                      {r.summary}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleCite(r)}
                  className={`flex-shrink-0 text-[11px] font-bold px-2.5 py-1.5 rounded-lg transition-all ${
                    cited.has(r.id)
                      ? 'bg-[#d8e6de] text-[#4f7a6a] dark:bg-[#2c463c]/30 dark:text-[#7fa595]'
                      : 'bg-[#f3e3c0] text-[#8f6122] dark:bg-[#553913]/30 dark:text-[#e0b866] hover:bg-[#e9cf98] dark:hover:bg-[#553913]/50'
                  }`}
                >
                  {cited.has(r.id) ? t('tribunal.jur.cited') : t('tribunal.jur.cite')}
                </button>
              </div>
              {r.url && (
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-[11px] text-[#5a7891] hover:underline mt-1 block"
                >
                  {t('tribunal.jur.view_fiche')}
                </a>
              )}
            </div>
          ))}
        </div>
      )}

      {results.length === 0 && !loading && query && (
        <p className="text-xs text-muted-foreground text-center py-4">
          {t('tribunal.jur.no_results')}
        </p>
      )}

      <p className="text-[11px] text-muted-foreground">
        {t('tribunal.jur.tip')}
      </p>
    </div>
  );
}

// ── Verdict ───────────────────────────────────────────────────────────────────

/** Le climax : révélation en plusieurs temps (greffier → verdict → issue → scores → motivation). */
function VerdictReveal({ trial, myRole }: { trial: Trial; myRole: Side | null }) {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const [showMotivation, setShowMotivation] = useState(false);
  const v = trial.verdict!;
  const scores = trial.scores ?? v.scores ?? null;
  const pScore = useCountUp(scores?.prosecution ?? 0, reduce ? 0 : 1600);
  const dScore = useCountUp(scores?.defense ?? 0, reduce ? 0 : 1600);

  const winner = v.winner;
  const outcome =
    winner === 'draw' || !winner ? t('tribunal.verdict_reveal.draw') :
    myRole                      ? (winner === myRole ? t('tribunal.verdict_reveal.you_won') : t('tribunal.verdict_reveal.you_lost')) :
    winner === 'prosecution'    ? t('tribunal.verdict_reveal.winner_prosecution') :
                                  t('tribunal.verdict_reveal.winner_defense');
  const outcomeTone =
    !myRole || winner === 'draw' || !winner ? 'bg-[#f8eedb] text-[#6e4a1a] dark:bg-[#33220b]/30 dark:text-[#e0b866]' :
    winner === myRole                        ? 'bg-[#e9f1ec] text-[#3d5f52] dark:bg-[#1b2c25]/30 dark:text-[#9cbfae]' :
                                               'bg-muted text-foreground';

  const step = (i: number) => reduce
    ? { initial: { opacity: 0 }, animate: { opacity: 1 } }
    : { initial: { opacity: 0, y: 12 }, animate: { opacity: 1, y: 0 }, transition: { delay: i * 0.5, duration: 0.5 } };

  const bar = (label: string, score: number, raw: number, cls: string, isWinner: boolean) => (
    <div>
      <div className="flex items-baseline justify-between mb-1">
        <span className={`text-xs font-bold ${isWinner ? 'text-foreground' : 'text-muted-foreground'}`}>{label}</span>
        <span className="text-lg font-bold tabular-nums text-foreground">{score}<span className="text-xs text-muted-foreground">/100</span></span>
      </div>
      <div className="h-2 rounded-full bg-muted overflow-hidden">
        <motion.div
          className={`h-full rounded-full ${cls}`}
          initial={{ width: reduce ? `${raw}%` : 0 }}
          animate={{ width: `${raw}%` }}
          transition={{ delay: reduce ? 0 : 1.6, duration: reduce ? 0 : 1.2, ease: 'easeOut' }}
        />
      </div>
    </div>
  );

  return (
    <section
      aria-labelledby="verdict-title"
      className="my-6 mx-auto max-w-2xl bg-card rounded-2xl overflow-hidden border border-border shadow-lg"
    >
      {/* Le prononcé, en estampe : le Président abat son marteau */}
      <figure className="m-0">
        <div className="relative h-56 sm:h-64 overflow-hidden border-b border-[#8a7656]">
          <CourtDiorama focus="judge" expressions={{ judge: 'strike' }} parallax={false} showWitness={false} span={1000} />
        </div>
        <figcaption className="flex justify-between gap-3 px-4 py-2 font-['IM_Fell_English',Georgia,serif] text-[13px] tracking-wide text-[#3b2a1a] bg-[#efe3c4]">
          <span>{t('tribunal.verdict_reveal.plate_caption')}</span>
        </figcaption>
      </figure>
      <div className="p-6">
      <motion.p {...step(0)} className="text-center text-xs italic text-muted-foreground mb-3">
        {t('tribunal.verdict_reveal.deliberated')}
      </motion.p>
      <motion.h2 {...step(1)} id="verdict-title" className="text-center font-['IM_Fell_English',Georgia,serif] font-normal text-5xl text-foreground">
        {VERDICT_KEYS[v.verdict] ? t(`tribunal.verdict_values.${VERDICT_KEYS[v.verdict]}`) : v.verdict}
      </motion.h2>
      <motion.div {...step(2)} className={`mt-4 text-center text-sm font-bold rounded-xl px-4 py-2.5 ${outcomeTone}`}>
        {outcome}
      </motion.div>
      {v.sentence && (
        <motion.p {...step(2.5)} className="mt-3 text-center text-sm text-foreground">
          <strong>{t('tribunal.verdict_reveal.sentence')} :</strong> {v.sentence}
        </motion.p>
      )}

      {scores && (
        <motion.div {...step(3)} className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {bar(trial.prosecution_name || t('tribunal.seats.prosecution'), pScore, scores.prosecution ?? 0, 'bg-[#b5523b]', winner === 'prosecution')}
          {bar(trial.defense_name || t('tribunal.seats.defense'), dScore, scores.defense ?? 0, 'bg-[#5a7891]', winner === 'defense')}
        </motion.div>
      )}

      {v.appreciation && (
        <motion.div {...step(4)} className="mt-5">
          <h3 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{t('tribunal.verdict_reveal.appreciation')}</h3>
          <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">{v.appreciation}</p>
        </motion.div>
      )}

      {v.motivation && (
        <motion.div {...step(4.5)} className="mt-4">
          <button
            onClick={() => setShowMotivation(s => !s)}
            aria-expanded={showMotivation}
            className="text-xs font-bold text-[#8f6122] dark:text-[#d9a441] hover:underline"
          >
            {showMotivation ? t('tribunal.verdict_reveal.hide_motivation') : t('tribunal.verdict_reveal.show_motivation')}
          </button>
          {showMotivation && (
            <p className="mt-2 text-sm text-foreground/90 leading-relaxed whitespace-pre-wrap">{v.motivation}</p>
          )}
        </motion.div>
      )}

      {trial.generated_case?.famous?.real_outcome && (
        <motion.div
          {...step(5)}
          className="mt-6 p-5 text-[#3b2a1a] bg-[#efe3c4] shadow-[inset_0_0_0_1px_#8a7656,inset_0_0_0_6px_#efe3c4,inset_0_0_0_7px_#8a7656]"
        >
          <div className="font-['IM_Fell_English',Georgia,serif] text-[13px] tracking-[.1em] uppercase">
            {t('tribunal.famous.second_print')} · {t('tribunal.famous.history_label', { date: new Date(trial.generated_case.famous.date).toLocaleDateString(getDateLocale(), { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' }) })}
          </div>
          <div className="mt-1 font-['IM_Fell_English',Georgia,serif] text-4xl">{trial.generated_case.famous.real_verdict}</div>
          <p className="mt-2 text-sm leading-relaxed">{trial.generated_case.famous.real_outcome}</p>
          {(() => {
            const real = trial.generated_case.famous.real_verdict?.toUpperCase() ?? '';
            const same = real.includes('NON') || real.includes('NOT')
              ? /NON|NOT/.test(v.verdict.toUpperCase())
              : !/NON|NOT/.test(v.verdict.toUpperCase());
            return (
              <p className="mt-4 inline-block -rotate-2 border-2 border-[#9b2a25] px-3 py-1.5 font-['IM_Fell_English',Georgia,serif] text-base text-[#9b2a25]">
                {same ? t('tribunal.famous.same_as_history_plain') : t('tribunal.famous.rewrote_history_plain')}
              </p>
            );
          })()}
        </motion.div>
      )}

      <Link to="/tribunal" className="block text-center text-xs text-muted-foreground hover:text-foreground underline mt-6">
        {t('tribunal.room.other_cases')}
      </Link>
      </div>
    </section>
  );
}

// ── Join / Invite modals ──────────────────────────────────────────────────────

const modalCardCls   = 'bg-background rounded-3xl w-full max-w-md overflow-hidden border border-border shadow-2xl';
const modalHeaderCls = 'px-7 py-5 border-b border-border bg-gradient-to-br from-[#f8eedb] to-background dark:from-[#33220b]/30';
const inputCls       = 'w-full rounded-xl px-4 py-3 text-sm bg-background text-foreground border border-border placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#e0b866]';
const ghostBtnCls    = 'py-3 rounded-xl text-sm font-semibold text-muted-foreground hover:text-foreground border border-border transition-colors';
const primaryBtnCls  = 'py-3 rounded-xl text-white text-sm font-bold bg-[#a8742a] hover:bg-[#8f6122] shadow-md shadow-[#a8742a]/25 disabled:opacity-50 transition-colors';

function ModalShell({
  labelledBy, onClose, children,
}: { labelledBy: string; onClose: () => void; children: React.ReactNode }) {
  useEscape(true, onClose);
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/45 backdrop-blur-sm"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <motion.div
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        initial={{ opacity: 0, scale: 0.94, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 280, damping: 28 }}
        className={modalCardCls}
      >
        {children}
      </motion.div>
    </motion.div>
  );
}

/**
 * Rejoindre le procès — réservé aux comptes SmartLawyer.
 * Non connecté : on propose Google / Microsoft (les mêmes fournisseurs que le site).
 * Connecté     : choix du siège, l'identité vient du compte.
 */
function JoinModal({
  trial, initialRole, onJoined, onClose,
}: {
  trial: Trial;
  /** Siège cliqué dans la salle d'attente. */
  initialRole?: Side | null;
  onJoined: (role: Side, uid: string, name: string) => void;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { user, loading: authLoading, error: authError, loginWithGoogle, loginWithMicrosoft } = usePublicAuth();

  const prosAvail = !trial.prosecution_taken;
  const defAvail  = !trial.defense_taken;

  const [role,    setRole]    = useState<Side>(
    initialRole && (initialRole === 'prosecution' ? prosAvail : defAvail) ? initialRole : prosAvail ? 'prosecution' : 'defense',
  );
  const [name,    setName]    = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // Pré-remplit avec le nom du compte
  useEffect(() => {
    if (user && !name) setName(user.displayName || user.email?.split('@')[0] || '');
    // volontairement limité à `user` : ne pas écraser une saisie en cours
  }, [user]);

  const handleJoin = async () => {
    if (!user) return;
    setLoading(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error(t('tribunal.join_modal.session_expired'));

      const r = await fetch(withLang(`/api/trials/${trial.id}/join`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ role, display_name: name.trim() || undefined }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || t('tribunal.join_modal.error'));
      }
      const d = await r.json();
      localStorage.setItem(`trial_${trial.id}_uid`,  d.user_uid);
      localStorage.setItem(`trial_${trial.id}_name`, d.display_name);
      localStorage.setItem(`trial_${trial.id}_role`, role);
      onJoined(role, d.user_uid, d.display_name);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const seatBtn = (side: Side, avail: boolean, takenBy: string | null) => {
    const isP = side === 'prosecution';
    const selected = role === side && avail;
    return (
      <button
        onClick={() => avail && setRole(side)}
        disabled={!avail}
        aria-pressed={selected}
        className={`p-4 rounded-xl border-2 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
          selected
            ? (isP ? 'border-[#9b3a2e] bg-[#f6e9e3] dark:bg-[#3a150f]/30' : 'border-[#3d5a73] bg-[#e9eef1] dark:bg-[#18242d]/30')
            : 'border-border bg-background'
        }`}
      >
        <div className="text-2xl mb-1" aria-hidden="true">{isP ? '⚔️' : '🛡️'}</div>
        <div className={`text-sm font-bold ${isP ? 'text-[#9b3a2e] dark:text-[#c47a68]' : 'text-[#3d5a73] dark:text-[#8aa3b8]'}`}>
          {t(isP ? 'tribunal.seats.prosecution' : 'tribunal.seats.defense')}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">
          {takenBy ? t('tribunal.join_modal.taken_by', { name: takenBy }) : t('tribunal.join_modal.available')}
        </div>
      </button>
    );
  };

  return (
    <ModalShell labelledBy="join-title" onClose={onClose}>
      <div className={modalHeaderCls}>
        <h2 id="join-title" className="font-serif text-xl font-bold text-foreground mb-0.5">{t('tribunal.join_modal.title')}</h2>
        <p className="text-sm text-muted-foreground">{trial.title}</p>
      </div>

      <div className="p-7">
        {!user ? (
          /* ── Non connecté : passage obligé par le compte SmartLawyer ── */
          <div className="space-y-4">
            <div className="flex items-center gap-3 mb-1">
              <SpeakerAvatar role="judge" size={44} />
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t('tribunal.join_modal.login_intro')}
              </p>
            </div>

            <button
              onClick={loginWithGoogle}
              disabled={authLoading}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl text-sm font-semibold bg-white text-gray-800 hover:bg-gray-50 border border-gray-200 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0012 23z"/>
                <path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 010-4.22V7.05H2.18a11 11 0 000 9.9l3.66-2.84z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1a11 11 0 00-9.82 6.05l3.66 2.84C6.71 7.29 9.14 5.38 12 5.38z"/>
              </svg>
              {t('tribunal.join_modal.google')}
            </button>

            <button
              onClick={loginWithMicrosoft}
              disabled={authLoading}
              className="w-full flex items-center justify-center gap-3 py-3.5 rounded-xl text-sm font-semibold bg-white text-gray-800 hover:bg-gray-50 border border-gray-200 transition-colors disabled:opacity-50"
            >
              <svg className="w-5 h-5" viewBox="0 0 23 23" aria-hidden="true">
                <path fill="#f25022" d="M1 1h10v10H1z"/><path fill="#7fba00" d="M12 1h10v10H12z"/>
                <path fill="#00a4ef" d="M1 12h10v10H1z"/><path fill="#ffb900" d="M12 12h10v10H12z"/>
              </svg>
              {t('tribunal.join_modal.microsoft')}
            </button>

            {(authError || error) && <p role="alert" className="text-sm text-[#9b3a2e]">{authError || error}</p>}

            <p className="text-[11px] text-muted-foreground text-center leading-relaxed pt-1">
              {t('tribunal.join_modal.invite_later')}
            </p>

            <button onClick={onClose} className={`w-full ${ghostBtnCls}`}>
              {t('tribunal.join_modal.later')}
            </button>
          </div>
        ) : (
          /* ── Connecté : choix du siège ── */
          <div className="space-y-4">
            <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl bg-[#f8eedb] dark:bg-[#33220b]/30 border border-[#e9cf98] dark:border-[#6e4a1a]">
              {user.photoURL
                ? <img src={user.photoURL} alt="" className="w-8 h-8 rounded-full" />
                : <div className="w-8 h-8 rounded-full bg-[#a8742a] text-white flex items-center justify-center text-xs font-bold" aria-hidden="true">
                    {(user.displayName || user.email || '?').charAt(0).toUpperCase()}
                  </div>}
              <div className="min-w-0">
                <div className="text-xs font-bold text-foreground truncate">{user.displayName || user.email}</div>
                <div className="text-[11px] text-muted-foreground">{t('tribunal.join_modal.verified')}</div>
              </div>
            </div>

            <div>
              <div className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{t('tribunal.join_modal.your_role')}</div>
              <div className="grid grid-cols-2 gap-3">
                {seatBtn('prosecution', prosAvail, trial.prosecution_name)}
                {seatBtn('defense',     defAvail,  trial.defense_name)}
              </div>
            </div>

            <div>
              <label htmlFor="join-name" className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {t('tribunal.join_modal.display_name')}
              </label>
              <input
                id="join-name"
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('tribunal.join_modal.name_placeholder')}
                className={inputCls}
                onKeyDown={e => { if (e.key === 'Enter') handleJoin(); }}
              />
            </div>

            {error && <p role="alert" className="text-sm text-[#9b3a2e]">{error}</p>}

            <div className="flex gap-3 pt-1">
              <button onClick={onClose} className={`flex-1 ${ghostBtnCls}`}>
                {t('common.cancel')}
              </button>
              <button
                onClick={handleJoin}
                disabled={loading || (!prosAvail && !defAvail)}
                className={`flex-1 ${primaryBtnCls}`}
              >
                {loading ? '…' : t('tribunal.join_modal.enter')}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

/**
 * Inviter un confrère sur le siège adverse resté libre.
 * Réservé à l'avocat déjà assis (le backend le revérifie).
 */
function InviteModal({
  trial, freeRole, onClose,
}: {
  trial: Trial;
  freeRole: Side;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const [emails,  setEmails]  = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [sent,    setSent]    = useState<string[]>([]);

  const parsed = emails
    .split(/[\s,;]+/)
    .map(e => e.trim())
    .filter(Boolean);

  const handleSend = async () => {
    if (!parsed.length) { setError(t('tribunal.invite.error_no_email')); return; }
    setLoading(true); setError('');
    try {
      const token = await getIdToken();
      if (!token) throw new Error(t('tribunal.join_modal.session_expired'));

      const r = await fetch(withLang(`/api/trials/${trial.id}/invite`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ emails: parsed, message: message.trim() }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || t('tribunal.invite.error_send'));
      }
      const d = await r.json();
      setSent(d.sent ?? parsed);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ModalShell labelledBy="invite-title" onClose={onClose}>
      <div className={`${modalHeaderCls} flex items-center gap-3`}>
        <SpeakerAvatar role={freeRole} size={40} />
        <div>
          <h2 id="invite-title" className="font-serif text-xl font-bold text-foreground mb-0.5">{t('tribunal.invite.title')}</h2>
          <p className="text-sm text-muted-foreground">
            <Trans
              i18nKey="tribunal.invite.free_seat"
              values={{ role: freeRole === 'prosecution' ? t('tribunal.seats.prosecution') : t('tribunal.seats.defense') }}
              components={{ strong: <strong className="text-foreground" /> }}
            />
          </p>
        </div>
      </div>

      <div className="p-7">
        {sent.length > 0 ? (
          <div className="text-center space-y-4">
            <div className="text-4xl" aria-hidden="true">📨</div>
            <p className="text-sm text-foreground font-semibold">
              {t('tribunal.invite.sent', { count: sent.length })}
            </p>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {sent.join(', ')}<br />
              {t('tribunal.invite.opens_when_taken')}
            </p>
            <button onClick={onClose} className={`w-full ${primaryBtnCls}`}>
              {t('tribunal.invite.perfect')}
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <label htmlFor="invite-emails" className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {t('tribunal.invite.emails')} <span className="normal-case tracking-normal font-semibold">{t('tribunal.invite.emails_hint')}</span>
              </label>
              <input
                id="invite-emails"
                type="text"
                value={emails}
                onChange={e => setEmails(e.target.value)}
                placeholder={t('tribunal.invite.emails_placeholder')}
                className={inputCls}
              />
            </div>

            <div>
              <label htmlFor="invite-note" className="block text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">
                {t('tribunal.invite.note')} <span className="normal-case tracking-normal font-semibold">{t('tribunal.modal.optional')}</span>
              </label>
              <textarea
                id="invite-note"
                value={message}
                onChange={e => setMessage(e.target.value.slice(0, 500))}
                rows={3}
                placeholder={t('tribunal.invite.note_placeholder')}
                className={`${inputCls} resize-none`}
              />
            </div>

            {error && <p role="alert" className="text-sm text-[#9b3a2e]">{error}</p>}

            <div className="flex gap-3">
              <button onClick={onClose} className={`flex-1 ${ghostBtnCls}`}>
                {t('common.cancel')}
              </button>
              <button
                onClick={handleSend}
                disabled={loading || !parsed.length}
                className={`flex-1 ${primaryBtnCls}`}
              >
                {loading ? '…' : t('tribunal.invite.send')}
              </button>
            </div>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

// ── Page principale ────────────────────────────────────────────────────────────

const EPOCH = '1970-01-01T00:00:00Z';
const SALLE_KEY = 'tribunal_salle';

export default function TrialRoom() {
  const { t } = useTranslation();
  const reduce = useReducedMotion();
  const { trialId } = useParams<{ trialId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const [trial,       setTrial]       = useState<Trial | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');
  const [showJoin,    setShowJoin]    = useState(false);
  const [joinSide,    setJoinSide]    = useState<Side | null>(null);
  const [showInvite,  setShowInvite]  = useState(false);
  const [showInfo,    setShowInfo]    = useState(false);
  const [myRole,      setMyRole]      = useState<Side | null>(null);
  const [unread,      setUnread]      = useState(0);
  // Salle en fond + gros plans (préférence du lecteur, activée par défaut)
  const [salle,       setSalle]       = useState(() => readLocal(SALLE_KEY) !== '0');
  // Dossier (panneau desktop / feuille mobile) et pupitre
  const [dossierTab,  setDossierTab]  = useState<DossierTab>('witnesses');
  const [sheetOpen,   setSheetOpen]   = useState(false);
  const [mode,        setMode]        = useState<ComposeMode>('plead');
  const [witnessId,   setWitnessId]   = useState<string | null>(null);
  const [evidenceId,  setEvidenceId]  = useState<string | null>(null);
  const composerRef  = useRef<HTMLTextAreaElement>(null);
  const logRef       = useRef<HTMLDivElement>(null);
  const atBottomRef  = useRef(true);
  // Pendant un défilement programmé (smooth), les événements scroll
  // intermédiaires ne doivent pas faire croire que le lecteur a quitté le bas.
  const autoScrollingRef = useRef(false);
  const prevCountRef = useRef(0);
  // Curseur du polling : horodatage SERVEUR du dernier message reçu (jamais
  // l'horloge du navigateur, qui peut être en avance et faire rater des messages).
  const lastSeenRef  = useRef(EPOCH);
  const inFlightRef  = useRef(false);
  const prevStatusRef = useRef<string | undefined>(undefined);
  const { user: authUser, loading: authResolving } = usePublicAuth();

  const toggleSalle = () => setSalle(s => { writeLocal(SALLE_KEY, s ? '0' : '1'); return !s; });

  // Restaure la session depuis localStorage (optimiste, le temps que le
  // compte réel et l'affaire se chargent — revérifié juste après).
  useEffect(() => {
    if (!trialId) return;
    const uid  = readLocal(`trial_${trialId}_uid`) || '';
    const role = readLocal(`trial_${trialId}_role`) as Side | null;
    if (uid && role) setMyRole(role);
  }, [trialId]);

  // Synchronise myRole sur la vérité serveur dès que l'affaire et l'identité
  // réelle sont connues — dans les deux sens : ça REND le siège si le serveur
  // le confirme (même si le local a été perdu/périmé), et ça le RETIRE si le
  // local prétend à tort qu'on est assis (compte différent, ancien essai
  // anonyme, siège réassigné...). Ne jamais se contenter de faire confiance
  // au localStorage indéfiniment dans un seul sens.
  useEffect(() => {
    if (!trialId || !trial || authResolving) return;

    // Attendre la réponse obtenue AVEC le jeton : sans lui, viewer_role vaut
    // toujours null et on effacerait à tort un siège réel.
    if (authUser && !trial._authed) return;
    const derived: Side | null = authUser ? (trial.viewer_role ?? null) : null;

    if (derived) {
      const name = (derived === 'prosecution' ? trial.prosecution_name : trial.defense_name) || '';
      writeLocal(`trial_${trialId}_uid`,  authUser!.uid);
      writeLocal(`trial_${trialId}_name`, name);
      writeLocal(`trial_${trialId}_role`, derived);
      setMyRole(derived);
    } else if (myRole) {
      try {
        localStorage.removeItem(`trial_${trialId}_uid`);
        localStorage.removeItem(`trial_${trialId}_name`);
        localStorage.removeItem(`trial_${trialId}_role`);
      } catch { /* stockage indisponible */ }
      setMyRole(null);
    }
  }, [trialId, trial, myRole, authUser, authResolving]);

  // Charge le procès complet
  // Un échec de rechargement en cours d'audience (502, coupure réseau) ne doit
  // jamais remplacer la salle par « Affaire introuvable » : seule la première
  // charge affiche l'erreur, les suivantes laissent le polling réessayer.
  const loadedRef = useRef(false);
  const fetchTrial = useCallback(async () => {
    if (!trialId) return;
    try {
      // Jeton Firebase : un avocat assis reçoit sa vue (stratégie, pièces,
      // témoignages) ; sans jeton, c'est la vue spectateur.
      const token = await getIdToken().catch(() => null);
      const r = await fetch(withLang(`/api/trials/${trialId}`), {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
        signal: timeoutSignal(15000),
      });
      if (!r.ok) {
        if (!loadedRef.current) setError(t(r.status === 404 ? 'tribunal.room.not_found' : 'tribunal.room.load_error'));
        return;
      }
      const data: Trial = await r.json();
      data._authed = !!token;
      loadedRef.current = true;
      setError('');
      // Fusion par id : un poll a pu ajouter des messages plus récents pendant
      // ce rechargement — on ne les efface pas (ni ne les ré-anime).
      setTrial(prev => {
        if (!prev) return data;
        const byId = new Map<string, TrialMessage>();
        for (const m of [...(prev.messages || []), ...(data.messages || [])]) byId.set(m.id, m);
        const messages = [...byId.values()].sort((a, b) => a.created_at.localeCompare(b.created_at));
        // Une réponse sans jeton (vue spectateur) arrivée après une réponse
        // authentifiée ne doit pas faire perdre la vue avocat.
        if (prev._authed && !data._authed) {
          return { ...data, messages, _authed: true, viewer_role: prev.viewer_role, generated_case: prev.generated_case };
        }
        return { ...data, messages };
      });
      const lastTs = data.messages?.length ? data.messages[data.messages.length - 1].created_at : null;
      if (lastTs && lastTs > lastSeenRef.current) lastSeenRef.current = lastTs;
    } catch {
      if (!loadedRef.current) setError(t('tribunal.room.load_error'));
    }
    finally { setLoading(false); }
  }, [trialId, t]);

  useEffect(() => { fetchTrial(); }, [fetchTrial]);
  // Une fois l'identité Firebase connue : recharge avec le jeton (vue avocat)
  useEffect(() => {
    if (!authResolving && authUser) fetchTrial();
  }, [authResolving, authUser?.uid, fetchTrial]);

  // Un poll : nouveaux messages + état (phase, tour, IA en cours, pièces, verdict)
  const pollOnce = useCallback(async () => {
    if (!trialId || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      // Délai max : une requête qui ne revient jamais bloquerait le polling
      // pour toujours (inFlightRef resterait vrai).
      const r = await fetch(withLang(`/api/trials/${trialId}/poll?since=${encodeURIComponent(lastSeenRef.current)}`), {
        signal: timeoutSignal(10000),
      });
      if (!r.ok) return;
      const data = await r.json();
      const newMessages: TrialMessage[] = data.messages ?? [];
      if (newMessages.length > 0) {
        const ts = newMessages[newMessages.length - 1].created_at;
        if (ts > lastSeenRef.current) lastSeenRef.current = ts;
      }
      setTrial(prev => {
        if (!prev) return prev;
        const known = new Set((prev.messages || []).map(m => m.id));
        const fresh = newMessages.filter(m => !known.has(m.id));
        return {
          ...prev,
          messages:        fresh.length ? [...(prev.messages || []), ...fresh] : prev.messages,
          current_phase:   data.current_phase,
          current_speaker: data.current_speaker,
          pending_actor:   data.pending_actor ?? null,
          status:          data.status,
          verdict:         data.verdict ?? prev.verdict,
          scores:          data.scores ?? prev.scores,
          evidence_list:   data.evidence_list ?? prev.evidence_list,
          // Sièges et noms : un siège pris par l'adversaire apparaît sans recharger
          prosecution_taken: data.prosecution_taken !== undefined ? data.prosecution_taken : prev.prosecution_taken,
          defense_taken:     data.defense_taken     !== undefined ? data.defense_taken     : prev.defense_taken,
          prosecution_name: data.prosecution_name !== undefined ? data.prosecution_name : prev.prosecution_name,
          defense_name:     data.defense_name     !== undefined ? data.defense_name     : prev.defense_name,
          phase_progress:   data.phase_progress   !== undefined ? data.phase_progress   : prev.phase_progress,
        };
      });
    } catch { /* silencieux : le prochain tick réessaie */ }
    finally { inFlightRef.current = false; }
  }, [trialId]);

  // Polling toutes les 2s (onglet visible uniquement), arrêté une fois le verdict rendu.
  // Le démarrage de l'audience (2e avocat assis) passe l'affaire en in_progress
  // côté serveur et fait apparaître les nouveaux sièges : on recharge l'affaire
  // complète lors d'un changement de statut.
  const status = trial?.status;
  useEffect(() => {
    if (!trialId || !status || status === 'completed') return;
    const id = setInterval(() => { if (!document.hidden) pollOnce(); }, 2000);
    return () => clearInterval(id);
  }, [trialId, status, pollOnce]);

  const kick = useCallback(() => { setTimeout(pollOnce, 700); }, [pollOnce]);

  useEffect(() => {
    if (!status) return;
    if (prevStatusRef.current !== undefined && prevStatusRef.current !== status) fetchTrial();  // sièges, noms, started_at
    prevStatusRef.current = status;
  }, [status, fetchTrial]);

  // Interrogatoire : le pupitre se met de lui-même en mode « Interroger »
  const currentPhase = trial?.current_phase;
  // Chaque phase ouvre le pupitre sur son geste naturel : un avocat qui passe
  // en clôture ne doit pas tomber sur « Verser une pièce » resté de la phase d'avant.
  useEffect(() => {
    setMode(currentPhase === 'examination' ? 'question' : currentPhase === 'evidence' ? 'evidence' : 'plead');
  }, [currentPhase]);

  // Gros plans : interventions des autres, à partir du chargement
  const spot = useSpotlightQueue({
    messages: trial?.messages ?? [],
    myRole,
    enabled: salle && !!trial && trial.status !== 'open',
    ready: !!trial,
  });

  // Défilement : on suit le bas du journal seulement si le lecteur y est déjà
  // (ou si c'est son propre message) ; sinon une pastille compte les nouveautés.
  const msgCount     = trial?.messages.length ?? 0;
  const pendingActor = trial?.status === 'in_progress' ? trial?.pending_actor ?? null : null;
  useEffect(() => {
    const el = logRef.current;
    if (!el || !trial) return;
    const added = msgCount - prevCountRef.current;
    const first = prevCountRef.current === 0;
    prevCountRef.current = msgCount;
    const last  = trial.messages[msgCount - 1];
    const mine  = !!myRole && last?.role === myRole && added > 0;
    if (first || atBottomRef.current || mine) {
      requestAnimationFrame(() => scrollToBottom(!first));
    } else if (added > 0) {
      setUnread(u => u + added);
    }
  }, [msgCount, pendingActor, status]);

  function scrollToBottom(smooth: boolean) {
    const el = logRef.current;
    if (!el) return;
    autoScrollingRef.current = smooth;
    atBottomRef.current = true;
    el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
    setUnread(0);
  }

  const handleLogScroll = () => {
    const el = logRef.current;
    if (!el) return;
    const dist = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (autoScrollingRef.current) {
      if (dist < 4) autoScrollingRef.current = false;
      return;
    }
    atBottomRef.current = dist < STICKY_BOTTOM_PX;
    if (atBottomRef.current && unread) setUnread(0);
  };

  // Un geste du lecteur reprend la main sur un défilement programmé en cours
  const handleUserScrollIntent = () => { autoScrollingRef.current = false; };

  // Ouvre automatiquement la modale « rejoindre » si on arrive avec ?join=1
  // (bouton « Je plaide » de l'affaire mise en avant sur /tribunal).
  useEffect(() => {
    if (!trial || authResolving || myRole || searchParams.get('join') !== '1') return;
    const stillOpen = trial.status === 'open' && (!trial.prosecution_taken || !trial.defense_taken);
    if (stillOpen) setShowJoin(true);
    const next = new URLSearchParams(searchParams);
    next.delete('join');
    setSearchParams(next, { replace: true });
  }, [trial, authResolving, myRole, searchParams, setSearchParams]);

  useEffect(() => { if (myRole) setShowJoin(false); }, [myRole]);
  useEscape(sheetOpen, () => setSheetOpen(false));
  useEscape(showInfo, () => setShowInfo(false));

  const handleJoined = async (role: Side) => {
    setShowJoin(false);
    // Attend que `trial` reflète le nouveau siège AVANT de marquer myRole : sinon
    // la reconciliation (qui compare myRole à trial.viewer_role)
    // tourne sur l'ancien instantané — encore vide — et efface le siège qu'on
    // vient tout juste de prendre.
    await fetchTrial();
    setMyRole(role);
  };

  // Dossier → pupitre : un clic sur un témoin / une pièce prépare l'intervention
  const questionWitness = (id: string) => {
    setMode('question'); setWitnessId(id); setSheetOpen(false);
    requestAnimationFrame(() => composerRef.current?.focus());
  };
  const fileEvidence = (id: string) => {
    setMode('evidence'); setEvidenceId(id); setSheetOpen(false);
    requestAnimationFrame(() => composerRef.current?.focus());
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center" role="status">
            <div className="mx-auto mb-4 w-fit animate-pulse"><TrialAvatar role="judge" size={72} /></div>
            <p className="text-muted-foreground text-sm">{t('tribunal.room.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !trial) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <PublicHeader />
        <div className="flex-1 flex items-center justify-center text-center px-4">
          <div>
            <p className="text-[#9b3a2e] mb-4">{error || t('tribunal.room.not_found')}</p>
            <Link to="/tribunal" className="text-sm text-[#a8742a] hover:underline">{t('tribunal.back_to_tribunal')}</Link>
          </div>
        </div>
      </div>
    );
  }

  const gc = trial.generated_case;
  const famous = gc?.famous;
  const isOpen = trial.status === 'open';
  const currentSpeaker = trial.status === 'in_progress' ? (trial.current_speaker as Side | null) : null;
  const canJoin = isOpen && (!trial.prosecution_taken || !trial.defense_taken) && !myRole;
  const freeSeat: Side | null =
    myRole === 'prosecution' && !trial.defense_taken     ? 'defense'     :
    myRole === 'defense'     && !trial.prosecution_taken ? 'prosecution' : null;
  const showVerdict = trial.status === 'completed' && !!trial.verdict;
  // Le verdict a sa propre mise en scène : on ne le duplique pas dans le journal.
  const logMessages = showVerdict ? trial.messages.filter(m => m.msg_type !== 'verdict') : trial.messages;
  const prog = trial.phase_progress && trial.phase_progress.phase === trial.current_phase ? trial.phase_progress : null;
  const isLawyerLive = !!myRole && trial.status === 'in_progress';
  const canQuestion = isLawyerLive && trial.current_phase === 'examination';
  const canFile = isLawyerLive && !!trial.current_speaker;
  const tabs = dossierTabs(trial, myRole);
  const turn = turnLine(t, trial, myRole);

  const lastRuling = [...trial.messages].reverse().find(m => m.role === 'judge');
  // Témoin à la barre : le dernier qui a répondu
  const barWitnessId = [...trial.messages].reverse().find(m => m.role === 'witness')?.metadata?.witness_id as string | undefined;
  const spotRole = spot.current?.role as 'judge' | 'witness' | 'prosecution' | 'defense' | undefined;
  const salleOn = salle && !isOpen;

  const dossierProps = {
    trial, myRole,
    canQuestion, selectedWitness: mode === 'question' ? witnessId : null, onQuestion: questionWitness,
    canFile, selectedEvidence: mode === 'evidence' ? evidenceId : null, onFile: fileEvidence,
  };

  return (
    <CourtContext.Provider value={courtOf(trial)}>
    <MotionConfig reducedMotion="user">
      <Helmet>
        <title>{t('tribunal.meta.room_title', { title: trial.title })}</title>
        {/* Fonte d'imprimerie ancienne des estampes (titres gravés) */}
        <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=IM+Fell+English:ital@0;1&display=swap" />
      </Helmet>

      <div className="h-dvh flex flex-col overflow-hidden bg-background">
        {/* ── En-tête ─────────────────────────────────────────────────── */}
        <header className="relative z-30 shrink-0 h-14 sm:h-16 border-b border-border bg-card px-2 sm:px-4 flex items-center gap-2 sm:gap-3">
          <Link
            to="/tribunal"
            aria-label={t('tribunal.back_to_tribunal')}
            className="shrink-0 w-10 h-10 rounded-lg hover:bg-muted flex items-center justify-center text-muted-foreground hover:text-foreground"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
          </Link>
          <div className="min-w-0 flex-1 2xl:flex-none 2xl:max-w-[340px] flex flex-col">
            <h1 className="font-bold text-foreground text-sm sm:text-[15px] truncate">{trial.title}</h1>
            <div className="flex items-center gap-2 min-w-0">
              {famous && (
                <button
                  type="button"
                  onClick={() => setShowInfo(s => !s)}
                  aria-expanded={showInfo}
                  className="shrink-0 text-[11px] font-bold uppercase tracking-wider text-[#553913] dark:text-[#e0b866] hover:underline"
                >
                  {famous.label}
                </button>
              )}
              {!isOpen && <PhaseStepper phase={trial.current_phase} />}
            </div>
          </div>

          {!isOpen && (
            <div className="hidden md:flex items-center gap-2 2xl:mx-auto">
              <PartyChip side="prosecution" name={trial.prosecution_name} active={currentSpeaker === 'prosecution'} mine={myRole === 'prosecution'} done={prog?.prosecution ?? 0} required={prog?.required ?? 0} />
              <PartyChip side="defense" name={trial.defense_name} active={currentSpeaker === 'defense'} mine={myRole === 'defense'} done={prog?.defense ?? 0} required={prog?.required ?? 0} />
            </div>
          )}

          <div className="ml-auto 2xl:ml-0 flex items-center gap-2 shrink-0">
            {trial.status === 'in_progress' && (
              <span className="hidden sm:inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-[#9b3a2e] dark:text-[#c47a68] bg-[#f6e9e3] dark:bg-[#3a150f]/30 px-2.5 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-[#b5523b] motion-safe:animate-pulse" aria-hidden="true" />
                {t('tribunal.live')}
              </span>
            )}
            {!isOpen && (
              <button
                onClick={toggleSalle}
                aria-pressed={salle}
                title={t('tribunal.court.salle_title')}
                className={`h-10 px-3 rounded-xl text-xs font-semibold flex items-center gap-2 border transition-colors ${
                  salle ? 'bg-foreground text-background border-foreground' : 'bg-card text-foreground border-border hover:bg-muted'
                }`}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="14" rx="2" /><path d="M7 21h10M12 18v3" /></svg>
                <span className="hidden sm:inline">{t('tribunal.court.salle')}</span>
              </button>
            )}
          </div>

          {/* Avertissement « grand procès » (bandeau tronqué → popover lisible) */}
          <AnimatePresence>
            {showInfo && famous && (
              <motion.div
                role="dialog"
                aria-label={famous.label}
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                className="absolute left-2 sm:left-14 top-full mt-2 w-[min(440px,calc(100vw-1rem))] rounded-xl border border-border bg-card shadow-xl p-4 text-sm leading-relaxed text-foreground"
              >
                <p>{famous.disclaimer}</p>
                <button onClick={() => setShowInfo(false)} className="mt-3 text-xs font-bold text-muted-foreground hover:text-foreground">{t('common.close')}</button>
              </motion.div>
            )}
          </AnimatePresence>
        </header>

        {isOpen ? (
          <WaitingRoom
            trial={trial}
            myRole={myRole}
            canJoin={canJoin}
            onJoin={(side) => { setJoinSide(side); setShowJoin(true); }}
            onInvite={() => setShowInvite(true)}
          />
        ) : (
          <div className="flex-1 min-h-0 flex">
            {/* ── Dossier permanent (écran large) ──────────────────────── */}
            <aside aria-label={t('tribunal.court.dossier')} className="hidden lg:flex flex-col w-[340px] xl:w-[360px] shrink-0 border-r border-border bg-card">
              <CaseDossier {...dossierProps} tab={tabs.includes(dossierTab) ? dossierTab : tabs[0]} onTab={setDossierTab} />
            </aside>

            {/* ── La salle, le procès-verbal, le pupitre ───────────────── */}
            <div className={`relative flex-1 min-w-0 flex flex-col overflow-hidden bg-muted/30`}>
              {/* La salle gravée : bandeau sur mobile, décor plein cadre sur grand écran */}
              {salleOn && (
                <div className="relative h-44 sm:h-56 shrink-0 border-b border-border lg:border-0 lg:absolute lg:inset-0 lg:h-auto">
                  <CourtDiorama
                    focus={spotRole ?? null}
                    currentSpeaker={currentSpeaker}
                    talking={spotRole ?? (pendingActor === 'witness' ? 'witness' : null)}
                    expressions={spot.current ? { [spotRole!]: expressionFor(spot.current) } : {}}
                    showWitness={trial.current_phase === 'examination' || pendingActor === 'witness' || spotRole === 'witness'}
                    witness={witnessVariant(spot.current?.role === 'witness' ? spot.current.metadata?.witness_id : barWitnessId, gc?.witnesses)}
                    judgeStrikeKey={lastRuling?.msg_type === 'ruling' ? lastRuling.id : null}
                  />
                </div>
              )}

              {/* Qui a la parole (petits écrans) */}
              {trial.status === 'in_progress' && (
                <div
                  role="status"
                  aria-live="polite"
                  className={`md:hidden relative z-10 shrink-0 flex items-center gap-2.5 px-3 py-2 border-b ${
                    turn.tone === 'prosecution' || (turn.tone === 'mine' && myRole === 'prosecution') ? 'bg-[#f6e9e3] border-[#e2c3b5] dark:bg-[#3a150f]/30 dark:border-[#5e2018]' :
                    turn.tone === 'defense'     || (turn.tone === 'mine' && myRole === 'defense')     ? 'bg-[#e9eef1] border-[#b9c6cd] dark:bg-[#18242d]/30 dark:border-[#22333f]' :
                    'bg-[#f8eedb] border-[#e9cf98] dark:bg-[#33220b]/30 dark:border-[#553913]'
                  }`}
                >
                  <TrialAvatar role={turn.tone === 'mine' ? myRole! : turn.tone === 'court' ? (pendingActor ?? 'judge') : turn.tone} size={32} speaking={turn.tone !== 'court'} thinking={!!pendingActor} />
                  <p className="text-[13px] font-bold text-foreground leading-snug flex-1 min-w-0">{turn.text}</p>
                </div>
              )}

              {/* Procès-verbal : vitre posée sur la salle (écran large) */}
              <main
                className={`relative z-10 flex-1 min-h-0 flex flex-col ${
                  salleOn ? 'lg:mx-auto lg:mt-[184px] lg:w-[min(46rem,calc(100%-19rem))] lg:rounded-t-2xl lg:bg-card/95 lg:backdrop-blur lg:shadow-2xl' : ''
                }`}
              >
                <div
                  ref={logRef}
                  onScroll={handleLogScroll}
                  onWheel={handleUserScrollIntent}
                  onTouchStart={handleUserScrollIntent}
                  onKeyDown={handleUserScrollIntent}
                  role="log"
                  aria-live={salleOn ? 'off' : 'polite'}
                  aria-relevant="additions"
                  aria-label={t('tribunal.room.log_label')}
                  className="flex-1 min-h-0 overflow-y-auto overscroll-contain"
                >
                  <div className={`mx-auto max-w-3xl px-3 sm:px-5 py-4 ${salleOn ? '' : 'lg:px-8'}`}>
                    {logMessages.length === 0 && !pendingActor ? (
                      <div className="flex flex-col items-center justify-center text-center py-16">
                        <TrialAvatar role="judge" size={72} />
                        <p className="text-muted-foreground mt-5">{t('tribunal.room.about_to_start')}</p>
                      </div>
                    ) : (
                      <Transcript
                        messages={logMessages}
                        witnesses={gc?.witnesses ?? []}
                        currentPhase={trial.current_phase}
                        status={trial.status}
                        pendingActor={pendingActor}
                        footer={showVerdict ? <VerdictReveal trial={trial} myRole={myRole} /> : null}
                      />
                    )}
                  </div>
                </div>

                {/* Nouveaux messages pendant qu'on relit plus haut */}
                <AnimatePresence>
                  {unread > 0 && (
                    <motion.button
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      onClick={() => scrollToBottom(true)}
                      className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 text-xs font-bold px-4 py-2 rounded-full bg-[#a8742a] text-white shadow-lg hover:bg-[#8f6122]"
                    >
                      {t('tribunal.room.new_messages', { count: unread })}
                    </motion.button>
                  )}
                </AnimatePresence>
              </main>

              {/* Gros plan sur l'intervenant */}
              {salleOn && (
                <Spotlight
                  msg={spot.current}
                  messages={trial.messages}
                  witnesses={gc?.witnesses ?? []}
                  onDone={spot.dismiss}
                />
              )}

              {/* Pupitre : toujours au-dessus, même pendant un gros plan */}
              {isLawyerLive && (
                <div className={`relative z-30 shrink-0 px-2 sm:px-3 pb-2 sm:pb-3 pt-2 ${salleOn ? 'lg:mx-auto lg:w-[min(46rem,calc(100%-19rem))] lg:px-0' : 'w-full max-w-3xl mx-auto'}`}>
                  <Composer
                    trial={trial}
                    myRole={myRole!}
                    mode={mode}
                    setMode={setMode}
                    witnessId={witnessId}
                    setWitnessId={setWitnessId}
                    evidenceId={evidenceId}
                    setEvidenceId={setEvidenceId}
                    textareaRef={composerRef}
                    onAction={fetchTrial}
                    onKick={kick}
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ── Barre du bas (mobile) : le dossier à un pouce ─────────────── */}
        {!isOpen && (
          <nav aria-label={t('tribunal.court.dossier')} className="lg:hidden relative z-30 shrink-0 grid border-t border-border bg-card" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
            {tabs.map(id => (
              <button
                key={id}
                onClick={() => { setDossierTab(id); setSheetOpen(true); }}
                className="h-14 flex flex-col items-center justify-center gap-0.5 text-[11px] font-semibold text-muted-foreground hover:text-foreground"
              >
                <DossierIcon tab={id} />
                {t(`tribunal.court.tabs.${id}`)}
              </button>
            ))}
          </nav>
        )}
      </div>

      {/* Feuille « dossier » (mobile) */}
      <AnimatePresence>
        {sheetOpen && !isOpen && (
          <motion.div key="sheet" className="lg:hidden fixed inset-0 z-50 flex flex-col justify-end" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <button type="button" aria-label={t('common.close')} className="absolute inset-0 bg-black/40 cursor-default" onClick={() => setSheetOpen(false)} />
            <motion.section
              role="dialog"
              aria-modal="true"
              aria-label={t(`tribunal.court.tabs.${dossierTab}`)}
              initial={reduce ? { opacity: 0 } : { y: '100%' }}
              animate={reduce ? { opacity: 1 } : { y: 0 }}
              exit={reduce ? { opacity: 0 } : { y: '100%' }}
              transition={{ type: 'spring', stiffness: 320, damping: 34 }}
              className="relative max-h-[85dvh] h-[85dvh] rounded-t-2xl bg-background flex flex-col overflow-hidden"
            >
              <div aria-hidden="true" className="mx-auto mt-2.5 w-10 h-1 rounded-full bg-border" />
              <div className="flex items-center px-4 pt-1">
                <h2 className="text-lg font-bold text-foreground">{t(`tribunal.court.tabs.${dossierTab}`)}</h2>
                <button onClick={() => setSheetOpen(false)} aria-label={t('common.close')} className="ml-auto w-11 h-11 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-muted">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
                </button>
              </div>
              <div className="flex-1 min-h-0">
                <CaseDossier {...dossierProps} tab={tabs.includes(dossierTab) ? dossierTab : tabs[0]} onTab={setDossierTab} hideTabs />
              </div>
            </motion.section>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showJoin && (
          <JoinModal trial={trial} initialRole={joinSide} onJoined={handleJoined} onClose={() => { setShowJoin(false); setJoinSide(null); }} />
        )}
        {showInvite && freeSeat && (
          <InviteModal trial={trial} freeRole={freeSeat} onClose={() => setShowInvite(false)} />
        )}
      </AnimatePresence>
    </MotionConfig>
    </CourtContext.Provider>
  );
}

function DossierIcon({ tab }: { tab: DossierTab }) {
  const p = { width: 20, height: 20, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  if (tab === 'witnesses') return <svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0116 0" /></svg>;
  if (tab === 'evidence') return <svg {...p}><path d="M3 7h6l2 2h10v10H3z" /></svg>;
  if (tab === 'strategy') return <svg {...p}><path d="M9 11l3 3 8-8M20 12v7H4V5h11" /></svg>;
  return <svg {...p}><path d="M4 5h16M4 10h16M4 15h10" /></svg>;
}
